import React, { useState, useEffect } from 'react';
import ViewLeadDialog from '../components/Lead/ViewLeadDialog';
import { Lead } from '../types/Lead';
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SearchIcon from '@mui/icons-material/Search';
import { useLeads } from '../hooks/useLeads';
import AddLeadDialog from '../components/Lead/AddLeadDialog';
import ImportLeadsDialog from '../components/Lead/ImportLeadsDialog';


const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const FilterChip = styled(Chip)(({ theme }) => ({
  transition: 'all 0.2s ease-in-out',
  fontWeight: 'bold',
  position: 'relative',
  '& .MuiChip-label': {
    padding: '0 12px',
  },
}));

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  borderRadius: '16px',
  marginRight: theme.spacing(1),
  border: 'none',
  padding: theme.spacing(0.5),
  '&.Mui-selected': {
    backgroundColor: 'transparent',
    '& .MuiChip-root': {
      transform: 'scale(1.05)',
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px currentColor`,
      filter: 'brightness(1.1)',
      '&::after': {
        content: '"✓"',
        position: 'absolute',
        top: '-8px',
        right: '-8px',
        backgroundColor: 'currentColor',
        color: 'white',
        borderRadius: '50%',
        width: '20px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: 'bold',
      }
    },
  },
  '&:hover': {
    backgroundColor: 'transparent',
    '& .MuiChip-root': {
      transform: 'scale(1.05)',
    },
  },
}));



type Order = 'asc' | 'desc';

function getComparator<Key extends keyof Lead>(
  order: Order,
  orderBy: Key,
): (a: Lead, b: Lead) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
  if (orderBy === 'createdAt' || orderBy === 'rescheduleDate') {
    const bDate = b[orderBy] ? new Date(b[orderBy] as string).getTime() : 0;
    const aDate = a[orderBy] ? new Date(a[orderBy] as string).getTime() : 0;
    return bDate - aDate;
  }

  const bValue = b[orderBy];
  const aValue = a[orderBy];

  if (bValue === undefined || bValue === null) return 1;
  if (aValue === undefined || aValue === null) return -1;

  if (typeof bValue === 'string' && typeof aValue === 'string') {
    return bValue.localeCompare(aValue);
  }

  return bValue > aValue ? 1 : bValue < aValue ? -1 : 0;
}

const Leads: React.FC = () => {
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof Lead>('createdAt');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [industryFilter, setIndustryFilter] = useState<string>('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const { leads, isLoading, error, refetch } = useLeads();
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  
  // Get unique industries from leads
  const industries = React.useMemo(() => {
    if (!leads) return [];
    const uniqueIndustries = Array.from(new Set(
      leads
        .map(lead => lead.industry)
        .filter((industry): industry is string => !!industry)
    ));
    return uniqueIndustries.sort();
  }, [leads]);

  useEffect(() => {
    if (leads) {
      const filtered = leads.filter(lead => {
        const matchesSearch = 
          lead.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lead.contactName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (lead.phone && lead.phone.includes(searchQuery));
        
        const matchesStatus = statusFilter.length === 0 || statusFilter.includes(lead.status);
        
        const matchesIndustry = !industryFilter || lead.industry === industryFilter;
        
        return matchesSearch && matchesStatus && matchesIndustry;
      });
      setFilteredLeads(filtered);
    }
  }, [leads, searchQuery, statusFilter, industryFilter]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'NEW':
        return 'primary';
      case 'CONVERTED':
        return 'success';
      case 'DEAD':
        return 'error';
      case 'RESCHEDULED':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (error) {
    return <Typography color="error">{`Error loading leads: ${error}`}</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Contacts
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={() => setOpenImportDialog(true)}
          >
            Import Contacts
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <TextField
            size="small"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0); // Reset to first page when searching
            }}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{ width: 300 }}
          />
          <ToggleButtonGroup
          value={statusFilter}
          onChange={(e, newFilters) => {
            setStatusFilter(newFilters);
            setPage(0); // Reset to first page when filtering
          }}
          size="small"
          exclusive={false}
          sx={{ ml: 1 }}
        >
          <StyledToggleButton value="NEW">
            <FilterChip
              label="New"
              color="primary"
              size="small"
            />
          </StyledToggleButton>
          <StyledToggleButton value="RESCHEDULED">
            <FilterChip
              label="Rescheduled"
              color="warning"
              size="small"
            />
          </StyledToggleButton>
          <StyledToggleButton value="CONVERTED">
            <FilterChip
              label="Converted"
              color="success"
              size="small"
            />
          </StyledToggleButton>
          <StyledToggleButton value="DEAD">
            <FilterChip
              label="Dead"
              color="error"
              size="small"
            />
          </StyledToggleButton>
        </ToggleButtonGroup>
        </Box>
        <TextField
          select
          size="small"
          label="Industry"
          value={industryFilter}
          onChange={(e) => {
            setIndustryFilter(e.target.value);
            setPage(0); // Reset to first page when filtering
          }}
          sx={{ minWidth: 250 }}
        >
          <MenuItem value="">All Industries</MenuItem>
          {industries.map((industry) => (
            <MenuItem key={industry} value={industry}>
              {industry}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'companyName'}
                  direction={orderBy === 'companyName' ? order : 'asc'}
                  onClick={() => {
                    const isAsc = orderBy === 'companyName' && order === 'asc';
                    setOrder(isAsc ? 'desc' : 'asc');
                    setOrderBy('companyName');
                  }}
                >
                  <Typography fontWeight="bold">Company Name</Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'contactName'}
                  direction={orderBy === 'contactName' ? order : 'asc'}
                  onClick={() => {
                    const isAsc = orderBy === 'contactName' && order === 'asc';
                    setOrder(isAsc ? 'desc' : 'asc');
                    setOrderBy('contactName');
                  }}
                >
                  <Typography fontWeight="bold">Contact Name</Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'phone'}
                  direction={orderBy === 'phone' ? order : 'asc'}
                  onClick={() => {
                    const isAsc = orderBy === 'phone' && order === 'asc';
                    setOrder(isAsc ? 'desc' : 'asc');
                    setOrderBy('phone');
                  }}
                >
                  <Typography fontWeight="bold">Phone Number</Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => {
                    const isAsc = orderBy === 'status' && order === 'asc';
                    setOrder(isAsc ? 'desc' : 'asc');
                    setOrderBy('status');
                  }}
                >
                  <Typography fontWeight="bold">Status</Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => {
                    const isAsc = orderBy === 'createdAt' && order === 'asc';
                    setOrder(isAsc ? 'desc' : 'asc');
                    setOrderBy('createdAt');
                  }}
                >
                  <Typography fontWeight="bold">Created</Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'rescheduleDateTime'}
                  direction={orderBy === 'rescheduleDateTime' ? order : 'asc'}
                  onClick={() => {
                    const isAsc = orderBy === 'rescheduleDateTime' && order === 'asc';
                    setOrder(isAsc ? 'desc' : 'asc');
                    setOrderBy('rescheduleDateTime');
                  }}
                >
                  <Typography fontWeight="bold">Reschedule</Typography>
                </TableSortLabel>
              </TableCell>

            </TableRow>
          </TableHead>
          <TableBody>
            {[...filteredLeads]
              .sort(getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((lead) => (
                <StyledTableRow 
                key={lead._id}
                onClick={() => setSelectedLead(lead)}
                hover
              >
                  <TableCell>{lead.companyName}</TableCell>
                  <TableCell>{lead.contactName}</TableCell>
                  <TableCell>{lead.phone || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      label={lead.status}
                      color={getStatusColor(lead.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(lead.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {lead.rescheduleDateTime ? (
                      <>
                        {new Date(lead.rescheduleDateTime).toLocaleDateString()}
                        {lead.isCallback && (
                          <Typography variant="body2" color="textSecondary">
                            {new Date(lead.rescheduleDateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        )}
                      </>
                    ) : '-'}
                  </TableCell>
                </StyledTableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredLeads.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <AddLeadDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
      />
      <ImportLeadsDialog
        open={openImportDialog}
        onClose={() => setOpenImportDialog(false)}
        onSuccess={() => {
          setOpenImportDialog(false);
          window.location.reload();
        }}
      />
      <ViewLeadDialog
        open={selectedLead !== null}
        onClose={() => setSelectedLead(null)}
        lead={selectedLead}
        onLeadUpdated={() => {
          refetch();
        }}
      />


    </Box>
  );
};

export default Leads;
